/**
 * Backend may append `drive_metadata_unavailable` when optional Drive API metadata
 * (e.g. parent folder name) cannot be read — often under `drive.file` OAuth scope.
 * Ingestion still uses the Google Sheets API for the spreadsheet body; this is not a failure.
 */
export function humanizeGoogleSheetsWarning(code: string): string {
    const c = (code || '').trim();
    if (c === 'drive_metadata_unavailable') {
        return 'Google Drive did not return extra file metadata (e.g. parent folder). This is common with the current OAuth scopes. Your sheet was still imported via the Google Sheets API.';
    }
    if (c.startsWith('tab_read_failed:')) {
        const rest = c.slice('tab_read_failed:'.length);
        return `Could not read a tab: ${rest}`;
    }
    if (c.startsWith('empty_tab_skipped:')) {
        const name = c.slice('empty_tab_skipped:'.length);
        return `Skipped empty tab: ${name}`;
    }
    if (c.startsWith('rows_truncated:')) {
        const name = c.slice('rows_truncated:'.length);
        return `Rows truncated for tab: ${name}`;
    }
    return c;
}

/** Full list for Details drawer / metadata. */
export function formatGoogleSheetsWarningsForDisplay(warnings: string[]): string {
    return warnings.map(humanizeGoogleSheetsWarning).join(' ');
}

/**
 * After a successful job, omit expected informational codes so we don't show a scary second toast.
 */
/** Shorter copy for post-import toast (Details drawer still uses ``humanizeGoogleSheetsWarning``). */
function humanizeGoogleSheetsWarningShort(code: string): string {
    const c = (code || '').trim();
    if (c === 'drive_metadata_unavailable') {
        return 'Optional Drive folder metadata missing — sheet still imported.';
    }
    if (c.startsWith('tab_read_failed:')) {
        return `Tab read issue: ${c.slice('tab_read_failed:'.length).slice(0, 80)}`;
    }
    if (c.startsWith('empty_tab_skipped:')) {
        return `Skipped empty tab: ${c.slice('empty_tab_skipped:'.length).slice(0, 60)}`;
    }
    if (c.startsWith('rows_truncated:')) {
        return `Rows truncated: ${c.slice('rows_truncated:'.length).slice(0, 60)}`;
    }
    return c.length > 120 ? `${c.slice(0, 117)}…` : c;
}

export function formatGoogleSheetsWarningsForToast(warnings: string[] | undefined): string | null {
    if (!warnings?.length) return null;
    const rest = warnings.filter((w) => (w || '').trim() !== 'drive_metadata_unavailable');
    if (!rest.length) return null;
    const line = rest.map(humanizeGoogleSheetsWarningShort).join(' · ');
    return line.length > 200 ? `${line.slice(0, 197)}…` : line;
}
